(ns ycweb.app.website.cart.pages.order-confirmation
  (:require
   [rum.core :as rum]
   [ycweb.app.website.cart.model :as model]
   [ycweb.app.website.cart.parts.common :as cart.common]
   [ycweb.app.website.tracking :as tracking]
   [ycweb.components.common :as cmn]
   [ycweb.components.feedback :as feedback]
   [ycweb.app.website.cart.pages.payment.eft :as eft]
   [ycweb.util.core :as u]
   [ycweb.util.urlstate :as urlstate]))

(defn- fire-purchase-tracking!
  "Push the GTM purchase payload once per order. Runs on mount AND update:
   the order-summary can arrive before this page mounts (SPA payment flows)
   or after (gateway-return full page loads) — fire-purchase-once! dedups."
  [state]
  #?(:cljs
     (let [[app-atom] (:rum/args state)
           summary (get @app-atom :order-summary)]
       (tracking/fire-purchase-once!
         (get-in summary [:order :id])
         (:ga_tracking summary))))
  state)

(def purchase-tracking-mixin
  {:did-mount fire-purchase-tracking!
   :did-update fire-purchase-tracking!})

(rum/defc order-confirm-content-collect < rum/reactive cmn/cursors
  [app-atom cart-summary-atom]
  (let [cart-summary (rum/react cart-summary-atom)
        delivery-method (get-in cart-summary [:cart :delivery :method])

        ;; Get the selected collection point based on method
        selected-point-uuid
        (if (= delivery-method "postnet")
          (get-in cart-summary [:cart :delivery :collection_point_uuid])
          (get-in cart-summary [:cart :delivery :collection_store_uuid]))

        ;; Get the appropriate collection points list
        collection-points
        (if (= delivery-method "postnet")
          (cmn/curact app-atom [:delivery-list :postnet_stores])
          (cmn/curact app-atom [:delivery-list :collection_stores]))

        ;; Find the selected point
        selected-point
        (->> collection-points
          (filter #(= (:uuid %) selected-point-uuid))
          first)

        store-name (or (:name selected-point) "your selected store")]
    [:<>
     [:p.yc-paragraph
      [:b
       "You've opted to collect your order from "
       [:a.text-link.js-prevent-default {:href (:maps_link selected-point) :target "_blank"}
        (u/glue ", " store-name (:city selected-point))]
       "."]]
     [:p.yc-paragraph
      "Your order will only be ready once you've received a \"ready for collection\" confirmation email from us. Present the QR code in the email to one of our sales assistants when collecting your order."]
     [:p.yc-paragraph
      [:b
       "Please wait for an email confirming it's ready to collect before coming in."]]]))

(rum/defc order-confirm-content-delivery []
  [:p {:class "yc-paragraph"}
   "Our team has been notified and they'll be putting your order together as quickly as possible. Once we've handed over your package to our delivery team, you'll be able to track your order. If you have any questions in the meantime, you're welcome to "
   [:a {:href "/help.htm" :class "text-link js-prevent-default"} "get hold of us"]
   "."])

(rum/defc order-confirm-content-digital []
  [:p.yc-paragraph
   "Your items will be delivered by email. If you have any questions in the meantime, you're welcome to "
   [:a.text-link.js-prevent-default {:href "/help.htm"}
    "get hold of us"] "."])

;; Order confirmation content component
(rum/defc order-confirmation-content < rum/reactive cmn/cursors
  [app-atom cart-summary-atom]
  (let [cart-summary (rum/react cart-summary-atom)
        delivery-method (get-in cart-summary [:cart :delivery :method])
        digital-only? (not (model/has-physical-items? cart-summary))
        collection? (contains? #{"collect" "postnet"} delivery-method)]
    [:div
     ;; Success feedback
     [:h2.yc-heading.yc-heading--default "What happens next?"]

     ;; Conditional content based on order type
     (cond
       digital-only? (order-confirm-content-digital)
       collection? (order-confirm-content-collect app-atom cart-summary-atom)
       :else (order-confirm-content-delivery))

     ;; Continue shopping button
     [:div
      [:a
       {:href "/"
        :on-click #(urlstate/redirect app-atom nil)
        :class (u/bem :button [:raised :default :secondary :bordered :has-icon-right])}
       [:span {:class "button__text"}
        "Continue shopping"
        [:i {:class "button__icon icon icon--is-dark icon--arrow-right"}]]]]]))

;; Order confirmation CTA sidebar component
(rum/defc order-confirmation-cta-sidebar < rum/reactive cmn/cursors
  [_app-atom _process-atom cart-summary-atom]
  (let [order-number (cmn/curact cart-summary-atom [:order :number])]
    [:div {:class "checkout-cta-wrapper js-checkout-cta-wrapper"}
     [:div {:class "checkout-cta"}
      [:div {:class "flex-grid-group flex-grid-group--flush-force flex-grid-group--valign-middle"}
       [:div {:class "flex-grid-block flex-grid-block--t-1-2 flex-grid-block--l-full"}
        [:div {:class "flex-grid-content"}
         [:p {:class "yc-paragraph yc-paragraph--large yc-paragraph--flush u-text-center"}
          [:b (str "Order no. " order-number)]]]]
       [:div {:class "flex-grid-block flex-grid-block--t-1-2 flex-grid-block--l-full"}
        [:div {:class "flex-grid-content"}
         [:a
          {:href "/"
           :on-click #(urlstate/redirect! "/")
           :class "button button--full button--cta checkout-cta__button js-prevent-default"}
          [:span {:class "button__text"} "Done"]]]]]]
     [:div {:class "checkout-secure-badge"}
      [:i {:class "icon icon--inline icon--lock icon--is-dark"}]
      [:span {:class "checkout-secure-badge__text"} "Safe and secure checkout."]]]))

;; Main order confirmation page
(rum/defc eft-paid-details < rum/reactive cmn/cursors [cart-summary-atom show-bank-details?]
  (let [order-number (cmn/curact cart-summary-atom [:order :number])]
    [:div
     (when show-bank-details?
       [:div
        [:h2 {:class "yc-heading yc-heading--default"} "Bank details for EFT payments"]
        (eft/banking-details-section order-number)])
     [:ul {:class "yc-list"}
      [:li [:b "Payment reference: " order-number]]
      [:li "Email proof of payment to: "
       [:a {:href "mailto:payments@yuppiechef.com" :class "text-link"} "payments@yuppiechef.com"]]
      [:li "Once we've received your proof of payment by email, we'll send you a confirmation email letting you know that we have it and will begin to process your order."]]]))

(rum/defc order-confirmation-page < rum/reactive cmn/cursors purchase-tracking-mixin
  [app-atom process-atom cart-summary-atom]
  "Step 5: Order confirmation page matching step-09.html design"
  (let [payment-confirmation (cmn/curact cart-summary-atom [:payment-confirmation])]
    [:main {:id "main-content" :class "yc-section"}
     [:div {:class "flex-grid-group"}

      [:div {:class "flex-grid-block flex-grid-block--ml-3-4 flex-grid-block--l-2-3"}
       [:div {:class "flex-grid-content"}
        (case payment-confirmation
          :eft-paid
          (list
            (rum/with-key
              (feedback/feedback :success
                "We have your order"
                "Thank you. We will process it once you have paid and we receive your proof of payment. Details below.")
              "eft-feedback")
            (rum/with-key (eft-paid-details cart-summary-atom false) "eft-details"))

          :eft-pay-later
          (list
            (rum/with-key
              (feedback/feedback :success
                "You have chosen to pay later for your order"
                "Thank you. We have your order and will process it once you have paid and we receive your proof of payment. Details below.")
              "eft-feedback")
            (rum/with-key (eft-paid-details cart-summary-atom true) "eft-details"))

          :zapper
          (feedback/feedback :success "Zapper payment successful!" "Your Zapper payment has been processed.")

          :snapscan
          (feedback/feedback :success "SnapScan payment successful!" "Your SnapScan payment has been processed.")

          (feedback/feedback :success "Payment successful" "We have your payment and will send you a confirmation by email."))
        (order-confirmation-content app-atom cart-summary-atom)]]

      [:div {:class "flex-grid-block flex-grid-block--t-full flex-grid-block--l-1-3"}
       [:div {:class "flex-grid-content"}
        (order-confirmation-cta-sidebar app-atom process-atom cart-summary-atom)]]]]))

(defmethod cart.common/checkout-step :order/confirmation
  [app-atom process-atom cart-atom]
  (order-confirmation-page app-atom process-atom (cmn/cursor-in app-atom [:order-summary])))